import { marketingHeroSrc } from '@/config/marketing-assets.config'
import { getAppUrl } from '@/config/marketing-nav.config'

const signupHref = getAppUrl('/signup')

export type AboutCard = {
  tag?: string
  title: string
  description: string
}

export const aboutContent = {
  eyebrow: 'About Podium Markets',
  heroTitle: 'Powered by AI, shaped by traders',
  heroLead:
    'Our team members built two of the leading options trading platforms - thinkorswim and tradeMONSTER - platforms trusted by millions of active traders. Ivy is the next leap forward: the intelligence layer those platforms always pointed toward but couldn’t yet become.',
  heroImage: marketingHeroSrc('about'),
  thesisEyebrow: 'Why We Are Building Ivy This Way',
  thesisTitle: 'What trading is becoming — and what it will demand',
  thesisLead:
    'We built the previous generation of tools for active traders. We saw what those tools couldn’t do. Ivy is the answer we’re no longer willing to wait for.',
  thesisCards: [
    {
      title: 'We built the desks. We know what they were missing.',
      description:
        'thinkorswim and tradeMONSTER gave a generation of active traders charting, options chains, and order entry. What they could not give - what no platform of that era could give was synthesis. The judgment layer. The thing a senior trader at an institutional desk does in their head every morning. Ivy is that intelligence layer.',
    },
    {
      title: 'The institutions that should have built it didn’t.',
      description:
        'Retail brokerages optimized for friction-free order entry. Finance media optimized for clicks. Large language models optimized for the median user. Each of those choices was rational for the company making it. None of them produced the intelligence layer and the tools the serious individual trader actually needed. Ivy is the answer for that need.',
    },
    {
      title: 'PhD research, shaped by active traders.',
      description:
        'Ivy combines PhD-level research in Computer Science, Machine Learning, Natural Language Processing and market microstructure with continuous input from a community of traders who actually put capital at risk. Neither half of that works alone. The research without the trader feedback produces a parlor trick. The trader feedback without the research produces a forum.',
    },
  ] satisfies AboutCard[],
  teamEyebrow: 'The Team',
  teamTitle: 'A track record across startups, brokerages, and the platforms traders already use',
  teamLead:
    'Ivy is not a first attempt. The people building it have shipped the products you have probably already traded on.',
  teamCards: [
    {
      tag: '2×',
      title: 'Two-time options-brokerage founders',
      description:
        'The leadership team has founded, scaled, and exited two options-focused brokerage platforms. Both were built for the active trader. Both were eventually sold to firms that millions of retail traders now use every day. We have done this before, and we know what the second time around is supposed to fix.',
    },
    {
      tag: 'ENG',
      title: 'PhD engineers, building at the scientific frontier',
      description:
        'Our engineering team includes PhD-level engineers who work alongside the best in the industry on architecting and building new scientific frontier models and inventing novel algorithms - not adapting someone else’s, not wrapping someone else’s API. Our team includes members that have shipped trading, gaming and market-data platforms across web, desktop, and mobile. Real-time order books, options chains, and broker connectivity at scale are not learning exercises.',
    },
    {
      tag: '50+',
      title: 'Operations with 50+ years of industry experience',
      description:
        'Compliance, brokerage operations, data partnerships, market connectivity - the unglamorous work that determines whether a fintech product can actually survive contact with regulators, exchanges, and aggregators. Our team has more than fifty combined years inside the firms that set the rules we operate under.',
    },
    {
      tag: 'UX',
      title: 'Design with large-scale fintech UX experience',
      description:
        'Designing a trading interface is not the same as designing a consumer app, and we will not pretend otherwise. Our design team has shipped fintech products to millions of users, on screens where the difference between a clear and an unclear interface is the difference between a good and a bad trade.',
    },
  ] satisfies AboutCard[],
  notBuildingEyebrow: 'What We Are Not Building',
  notBuildingTitle: 'The product is defined as much by what we refuse to ship',
  notBuildingLead:
    'Building features for active traders taught us the difference between features that look good in a pitch deck and features that actually survive contact with real traders.',
  notBuildingCards: [
    {
      title: 'No confetti',
      description:
        'We do not animate trades. We do not celebrate orders. The moment a trader puts capital at risk is not a moment to be congratulated for engagement. It is a moment to be helped to think clearly.',
    },
    {
      title: 'No push-to-trade',
      description:
        'Ivy will not notify you into a position. It will tell you what changed in the market and in your portfolio, and it will leave the decision where it belongs - with you.',
    },
    {
      title: 'No median-user hedging',
      description:
        'The job of a general-purpose model is to avoid being wrong for anyone. The job of Ivy is to be right for you. Those are different jobs, and we built Ivy to do the second one.',
    },
  ] satisfies AboutCard[],
  ctaTitle: 'The future of trading feels human again',
  ctaLead: 'Stop trading through disconnected tools and build your own financial Jarvis.',
  ctaPrimaryLabel: 'Get Started',
  ctaPrimaryHref: signupHref,
}
